import { ParticipantType } from "./types/common";
import { ChatMessage, Participant } from "./types/queries";

type ParticipantRef = Pick<Participant, "id" | "type">;

export type ReadReceiptPlan =
  | { action: "ignore" }
  | { action: "refetch" }
  | { action: "apply"; messageIds: string[] };

const isCounterparty = (participant: ParticipantRef) =>
  participant.type === ParticipantType.TALENT ||
  participant.type === ParticipantType.CUSTOMER;

/**
 * An expert's message is read only when the talent or customer is in readBy.
 * A talent or customer message is read when any other participant is in readBy.
 */
export const isReadByRecipient = (
  readBy: string[] | undefined,
  participants: readonly ParticipantRef[],
  currentUserId: string,
): boolean => {
  if (!readBy?.length || !currentUserId) return false;
  const me = participants.find(
    (participant) => participant.id === currentUserId,
  );
  if (
    me?.type === ParticipantType.TALENT ||
    me?.type === ParticipantType.CUSTOMER
  ) {
    return true;
  }
  return participants.some(
    (participant) =>
      isCounterparty(participant) && readBy.includes(participant.id),
  );
};

export const shouldMarkConversationRead = ({
  conversationId,
  lastMessageId,
  lastMessageSenderId,
  userId,
}: {
  conversationId?: string;
  lastMessageId?: string;
  lastMessageSenderId?: string;
  userId?: string;
}): boolean =>
  !!conversationId && !!lastMessageId && lastMessageSenderId !== userId;

/**
 * Own messages at or before the reader's cursor gain that reader.
 * A missing cursor, or two messages with the same sentAt, cannot be ordered here.
 */
export const planReadReceipt = (
  messages: readonly ChatMessage[],
  currentUserId: string,
  readerId: string,
  lastReadMessageId: string,
): ReadReceiptPlan => {
  if (!currentUserId || !readerId || readerId === currentUserId) {
    return { action: "ignore" };
  }

  const cursor = messages.find((message) => message.id === lastReadMessageId);
  if (!cursor) return { action: "refetch" };

  const sentAtCollision = messages.some(
    (message) => message.id !== cursor.id && message.sentAt === cursor.sentAt,
  );
  if (sentAtCollision) return { action: "refetch" };

  const messageIds = messages
    .filter(
      (message) =>
        message.senderId === currentUserId &&
        (message.id === cursor.id || message.sentAt < cursor.sentAt) &&
        !message.readBy?.includes(readerId),
    )
    .map((message) => message.id);

  return { action: "apply", messageIds };
};
